import type Highcharts from 'highcharts/highstock';
import { useCallback, useRef, useState } from 'react';

/**
 * Fixes the first-render glitch where x-axis labels overlap until the settings drawer is toggled.
 * The labels are measured before the container height and web font settle; a full chart update
 * re-lays them out (what a drawer toggle happens to do). This triggers that update once, after the
 * chart is created and fonts have loaded.
 *
 * Spread `refreshKey` into the chart-options `useMemo` deps and pass `onChartCreated` to
 * `<HighchartsReact callback={...} />`.
 */
export const useChartRefreshAfterMount = (): { refreshKey: number; onChartCreated: (chart: Highcharts.Chart) => void } => {
    const [refreshKey, setRefreshKey] = useState(0);
    const hasRefreshed = useRef(false);

    const onChartCreated = useCallback(() => {
        if (hasRefreshed.current) {
            return;
        }
        hasRefreshed.current = true;

        // Next frame: container height has settled by then.
        const bump = () => requestAnimationFrame(() => setRefreshKey((key) => key + 1));

        // Wait for the web font too so labels re-measure against it, not the fallback.
        const fontsReady = typeof document !== 'undefined' ? document.fonts?.ready : undefined;
        if (fontsReady) {
            fontsReady.then(bump, bump);
        } else {
            bump();
        }
    }, []);

    return { refreshKey, onChartCreated };
};
